import React, { useState, useEffect } from 'react';
import { Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSwappingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './DraggableList.styles.css';

type DraggableItem = {
  id: string;
  [key: string]: any;
};

type DraggableListProps<T extends DraggableItem> = {
  items: T[];
  onReorder: (newOrder: T[]) => void;
  renderItem: (item: T, index: number, isDragging: boolean) => React.ReactNode;
  emptyMessage?: string;
  className?: string;
  itemClassName?: string;
};

function SortableItem<T extends DraggableItem>({
  id,
  item,
  index,
  renderItem,
  itemClassName = "draggable-item",
}: {
  id: string;
  item: T;
  index: number;
  renderItem: (item: T, index: number, isDragging: boolean) => React.ReactNode;
  itemClassName?: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
    position: 'relative' as const,
  };

  return (
    <div
      ref={setNodeRef}
      className={`${itemClassName} ${isDragging ? 'is-dragging' : ''}`}
      style={style}
      {...attributes}
      {...listeners}
      data-index={index}
    >
      {renderItem(item, index, isDragging)}
    </div>
  );
}

function swapItemsInArray<T>(array: T[], index1: number, index2: number): T[] {
  const newArray = [...array];
  [newArray[index1], newArray[index2]] = [newArray[index2], newArray[index1]];
  return newArray;
}

function DraggableList<T extends DraggableItem>({
  items,
  onReorder,
  renderItem,
  emptyMessage,
  className = "draggable-list",
  itemClassName = "draggable-item",
}: DraggableListProps<T>): React.ReactElement {
  const { t } = useTranslation();
  const [internalItems, setInternalItems] = useState<T[]>(items);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [anyDragging, setAnyDragging] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setInternalItems(items);
  }, [items]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
    setAnyDragging(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setAnyDragging(false);
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = internalItems.findIndex(item => item.id === active.id);
      const newIndex = internalItems.findIndex(item => item.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = swapItemsInArray(internalItems, oldIndex, newIndex);
        setInternalItems(newItems);
        onReorder(newItems);
      }
    }
  };

  if (internalItems.length === 0) {
    return (
      <Empty
        description={emptyMessage || t('no-items', { ns: 'labels' })}
        className={`${className}-empty`}
      />
    );
  }

  const activeItem = activeId ? internalItems.find(item => item.id === activeId) : null;
  const activeIndex = activeItem ? internalItems.indexOf(activeItem) : -1;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext 
        items={internalItems.map(item => ({ id: item.id }))} 
        strategy={rectSwappingStrategy}
      >
        <div className={`${className} ${anyDragging ? 'dragging-active' : ''}`}>
          {internalItems.map((item, index) => (
            <SortableItem
              key={item.id}
              id={item.id}
              item={item}
              index={index}
              renderItem={renderItem}
              itemClassName={itemClassName}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay adjustScale={false}>
        {activeItem && activeIndex !== -1 ? (
          <div className={`${itemClassName} drag-overlay`}>
            {renderItem(activeItem, activeIndex, true)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default DraggableList;